import { Download, SlidersHorizontal, Upload } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { RenderingImage } from '../atoms/image-files';

type Props = {
  images: RenderingImage[];
  selectedImageId?: string;
  onSelectImage: (image: RenderingImage) => void;
  onUseAsSource: (image: RenderingImage) => void;
  onDownloadImage: (image: RenderingImage) => void;
  onEditImage: (image: RenderingImage) => void;
};

export const GeneratedImagesSection = ({
  images,
  selectedImageId,
  onSelectImage,
  onUseAsSource,
  onDownloadImage,
  onEditImage,
}: Props) => {
  const { t } = useTranslation();
  const itemRefs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    if (!selectedImageId) return;

    const frameId = requestAnimationFrame(() => {
      itemRefs.current.get(selectedImageId)?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [selectedImageId]);

  return (
    <section className="h-[170px] min-w-0 shrink-0 overflow-hidden bg-white px-6 py-1">
      <div className="flex items-center justify-between text-sm font-semibold text-slate-500">
        <span>{t('generated-images')}</span>
        <span>{t('generated-images-count', { count: images.length })}</span>
      </div>
      <div className="flex min-w-0 touch-pan-x items-start gap-4 overflow-x-auto overscroll-x-contain pb-3 [-webkit-overflow-scrolling:touch] [scrollbar-color:#cbd5e1_#f1f5f9] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar]:h-1.5">
        {images.map((image) => {
          const isGenerated = Boolean(image.renderedImage);
          const label = isGenerated
            ? image.renderingType === 'ai-rendering'
              ? t('ai-rendering')
              : image.magicEditMode === 'remove'
                ? t('remove')
                : t('freeEdit')
            : t('source-image');

          return (
            <div
              key={image.id}
              ref={(node) => {
                if (node) {
                  itemRefs.current.set(image.id, node);
                  return;
                }

                itemRefs.current.delete(image.id);
              }}
              className={cn(
                'h-[128px] w-[112px] flex-none overflow-hidden rounded-sm border bg-white text-left shadow-sm',
                selectedImageId === image.id ? 'border-primary-light' : 'border-slate-200'
              )}
            >
              <button
                type="button"
                className="block w-full text-left"
                onClick={() => onSelectImage(image)}
              >
                <div className="h-[66px] w-full overflow-hidden bg-slate-100">
                  <img
                    src={image.renderedImage || image.originalImage}
                    alt="Rendering"
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="px-2 pt-2 text-[11px] font-semibold text-slate-700">{label}</div>
              </button>

              {isGenerated ? (
                <div className="px-1.5 pb-2">
                  <div className="mt-1.5 flex justify-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUseAsSource(image);
                          }}
                          aria-label={t('use-as-input')}
                          className="flex !h-5 !w-7 min-w-0 items-center justify-center rounded-sm bg-slate-50 !px-0 py-0 text-primary-light hover:bg-slate-100"
                        >
                          <Upload aria-hidden className="!size-2.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={8}
                        className="rounded-sm border border-slate-200 px-3 py-2 text-center text-sm font-medium leading-5 text-slate-700 shadow-md"
                      >
                        {t('use-as-input')}
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadImage(image);
                          }}
                          aria-label={t('download')}
                          className="flex !h-5 !w-7 min-w-0 items-center justify-center rounded-sm bg-slate-50 !px-0 py-0 text-slate-500 hover:bg-slate-100"
                        >
                          <Download aria-hidden className="!size-2.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={8}
                        className="rounded-sm border border-slate-200 px-3 py-2 text-center text-sm font-medium leading-5 text-slate-700 shadow-md"
                      >
                        {t('download')}
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditImage(image);
                          }}
                          aria-label={t('image-editor-action')}
                          className="flex !h-5 !w-7 min-w-0 items-center justify-center rounded-sm bg-slate-50 !px-0 py-0 text-slate-500 hover:bg-slate-100"
                        >
                          <SlidersHorizontal aria-hidden className="!size-2.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={8}
                        className="rounded-sm border border-slate-200 px-3 py-2 text-center text-sm font-medium leading-5 text-slate-700 shadow-md"
                      >
                        {t('image-editor-action')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};
